import React, { useState } from 'react';
import {
  Button,
  Grid,
  Heading,
  IconButton,
  Panel,
  PanelProps
} from '@walmart-web/livingdesign-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft as ChevronLeftIcon } from '@livingdesign/icons';
import Help from '@livingdesign/pictograms/dist/svg/App_&_Web_Interface/Help.svg';
import RegistrationConsentMedicalForm from '@livingdesign/pictograms/dist/svg/Health_Forms_&_Office/RegistrationConsentMedicalForm.svg';
import TermsOfUse from '@livingdesign/pictograms/dist/svg/App_&_Web_Interface/TermsOfUse.svg';

import styles from './styles.module.css';
import HowDoesItWorkResources from './HowDoesItWorkResources/index.tsx';
import FaqResources from './FaqResources/index.tsx';
import SupportCenterResources from './SupportCenterResources/index.tsx';
import ResourcePanelItem from './ResourcePanelItem/index.tsx';
import useToggle from '../../hooks/useToggle.ts';

type Props = {} & Pick<PanelProps, 'onClose' | 'isOpen'>;

const ProductTestingResourcesPanel: React.FC<Props> = ({
  onClose,
  ...restProps
}) => {
  const [activeResourceIdx, setActiveResourceIdx] = useState<number | null>(
    null
  );
  const { value: seeAll, toggle: toggleSeeAll } = useToggle();
  const { t } = useTranslation('ihut');

  const handleClose = (e) => {
    onClose?.(e);
    setActiveResourceIdx(null);
  };

  const PANEL_DATA = [
    {
      key: 'howDoesItWork',
      title: t('howDoesItWork.title'),
      Component: HowDoesItWorkResources,
      icon: Help
    },
    {
      key: 'faq',
      title: t('faq.title'),
      Component: FaqResources,
      icon: RegistrationConsentMedicalForm
    },
    {
      key: 'supportCenter',
      title: t('supportCenter.title'),
      Component: SupportCenterResources,
      icon: TermsOfUse
    }
  ] as const;

  const getPanelTitle = () =>
    typeof activeResourceIdx === 'number' ? (
      <Grid className={styles.titleContainer}>
        <IconButton
          a11yLabel="Back Icon Button"
          size="medium"
          onClick={() => setActiveResourceIdx(null)}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Heading size="small">{PANEL_DATA[activeResourceIdx].title}</Heading>
      </Grid>
    ) : (
      t('productTestingResources')
    );

  const ActiveResourceComponent = ({
    idx,
    seeAll = false
  }: {
    idx: number;
    seeAll?: boolean;
  }) => {
    const { Component } = PANEL_DATA[idx];
    return <Component seeAll={seeAll} />;
  };

  return (
    <Panel
      title={getPanelTitle()}
      position="right"
      onClose={handleClose}
      actions={
        typeof activeResourceIdx === 'number' &&
        PANEL_DATA[activeResourceIdx].key === 'faq' && (
          <Button
            size="small"
            isFullWidth
            variant="secondary"
            onClick={toggleSeeAll}
          >
            {t(seeAll ? 'seeLess' : 'seeAll', { ns: 'common' })}
          </Button>
        )
      }
      {...restProps}
    >
      {activeResourceIdx === null ? (
        <Grid UNSAFE_className={styles.resourcesContainer}>
          {PANEL_DATA.map(({ key, title, icon }, idx) => (
            <ResourcePanelItem
              key={key}
              title={title}
              icon={icon}
              onClick={() => setActiveResourceIdx(idx)}
            />
          ))}
        </Grid>
      ) : (
        <ActiveResourceComponent idx={activeResourceIdx} seeAll={seeAll} />
      )}
    </Panel>
  );
};

export default ProductTestingResourcesPanel;
